/**
 * "Auto-follow counter": whether picking a counter (or moving its times) in the officer card
 * brings that counter's row into view in the grid and flashes its label. On by default.
 *
 * Personal to this device — someone who dislikes the page moving by itself switches it off
 * once and it stays off in every session. The soft hover highlight is not affected: it never moves anything.
 */
import { useSyncExternalStore } from 'react';
import { loadPref, savePref } from './prefStorage';

const KEY = 'ac_roster_auto_follow_counter';
const listeners = new Set<() => void>();

export function getAutoFollowCounter(): boolean {
  return loadPref(KEY) !== 'off';
}

export function setAutoFollowCounter(on: boolean): void {
  savePref(KEY, on ? 'on' : 'off');
  listeners.forEach((l) => l());
}

export function useAutoFollowCounter(): boolean {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    getAutoFollowCounter,
    () => true,
  );
}

/**
 * The grid row the officer card is pointing at (hover / focus on one of its rows), or null.
 * A window event rather than React state, so a hover never re-renders the roster grid.
 */
export const COUNTER_FOCUS_EVENT = 'roster:counter-focus';

export function announceCounterFocus(row: number | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<{ row: number | null }>(COUNTER_FOCUS_EVENT, { detail: { row } }));
}
